<script setup lang="ts">
// Meta blocked the connected Facebook profile until it clears the steps on facebook.com
// (ORP-26183) — New-Front's `MetaCheckpointBanner.vue`. Nothing is broken on our side, so the tone
// is warning rather than ConnectionLostBanner's danger, and there is no close button: it goes when
// the block does. While it shows, Meta steps out of ConnectionLostBanner (`useIntegrations`), since
// that one would tell the user to reconnect and this one says reconnecting does not help.
import { ref, watch } from 'vue'

import { registerIntegrationsMessages, useMetaCheckpoint } from '@/features/integrations'
import { useI18n, useLocaleStore } from '@/shared/i18n'
import { Icon, PlatformLogo, resolveStaticPlatformLogo, useToast } from '@/shared/ui'

const MESSAGE_KEY = 'integrations.checkpoint.title'
const { t, te } = useI18n()
const localeStore = useLocaleStore()
const toast = useToast()
const checkpoint = useMetaCheckpoint()
const messagesReady = ref(te(MESSAGE_KEY))
const inlineMessage = ref('')

watch(
  () => localeStore.locale,
  async (locale) => {
    messagesReady.value = te(MESSAGE_KEY)
    try {
      await registerIntegrationsMessages(locale)
      messagesReady.value = true
    } catch {
      messagesReady.value = false
    }
  },
  { immediate: true },
)

async function verify(): Promise<void> {
  inlineMessage.value = ''
  const outcome = await checkpoint.verify()
  if (outcome.status === 'blocked') inlineMessage.value = t('integrations.checkpoint.stillBlocked')
  else if (outcome.status === 'failed')
    inlineMessage.value = outcome.detail ?? t('integrations.checkpoint.verifyFailed')
  else if (outcome.status === 'cleared') toast.success(t('integrations.checkpoint.cleared'))
}
</script>

<template>
  <div
    v-if="messagesReady && checkpoint.visible.value"
    class="meta-checkpoint-banner flex w-full flex-col gap-md border-y border-accent/30 bg-accent-soft px-md py-sm md:flex-row md:items-center md:justify-between"
    role="alert"
    data-testid="meta-checkpoint-banner"
  >
    <div class="flex min-w-0 items-center gap-sm">
      <span
        class="relative grid h-8 w-8 flex-none place-items-center rounded-md border border-accent/30 bg-surface"
        aria-hidden="true"
      >
        <PlatformLogo
          platform="meta"
          :src="resolveStaticPlatformLogo('meta') ?? ''"
          alt=""
          :size="16"
        />
        <Icon
          name="circle-alert"
          :size="11"
          class="absolute -end-1 -top-1 rounded-full bg-surface text-accent"
        />
      </span>
      <div class="grid min-w-0 gap-2xs text-sm font-normal text-[var(--ui-color-checkpoint-text)]">
        <p class="m-0 font-semibold text-[var(--ui-color-checkpoint-heading)]">
          {{ t('integrations.checkpoint.title') }}
        </p>
        <i18n-t
          v-if="checkpoint.identity.value"
          keypath="integrations.checkpoint.bodyIdentity"
          scope="global"
          tag="span"
        >
          <template #identity
            ><strong class="font-semibold text-[var(--ui-color-checkpoint-heading)]">{{
              checkpoint.identity.value
            }}</strong></template
          >
        </i18n-t>
        <span v-else>{{ t('integrations.checkpoint.body') }}</span>
        <span
          v-if="inlineMessage"
          class="inline-flex items-center gap-2xs text-xs"
          data-testid="meta-checkpoint-inline"
        >
          <Icon name="info" :size="12" aria-hidden="true" />
          {{ inlineMessage }}
        </span>
      </div>
    </div>
    <div class="flex flex-none flex-wrap items-center gap-xs">
      <button
        type="button"
        class="inline-flex min-h-9 items-center justify-center gap-2xs rounded-md border border-accent/30 bg-surface px-sm py-xs text-sm font-medium text-[var(--ui-color-checkpoint-text)] transition-colors hover:bg-accent-soft focus-visible:outline focus-visible:outline-ring"
        data-testid="meta-checkpoint-open"
        @click="checkpoint.openFacebook"
      >
        {{ t('integrations.checkpoint.openFacebook') }}
        <Icon name="external-link" :size="12" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="inline-flex min-h-9 items-center justify-center gap-2xs rounded-md border border-accent bg-accent px-sm py-xs text-sm font-medium text-[var(--ui-color-checkpoint-button-fg)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-ring"
        :aria-busy="checkpoint.verifying.value || undefined"
        :disabled="!checkpoint.canVerify.value || checkpoint.verifying.value"
        data-testid="meta-checkpoint-verify"
        @click="verify"
      >
        <Icon
          :name="checkpoint.verifying.value ? 'rotate-left' : 'check'"
          :class="{ 'animate-spin': checkpoint.verifying.value }"
          :size="13"
          aria-hidden="true"
        />
        {{ t('integrations.checkpoint.verify') }}
      </button>
    </div>
  </div>
</template>
